import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { sanitizeSearchQuery } from '../utils/sanitizeSearchQuery';
import { US_STATES, CITIES_BY_STATE } from '../config/usLocationsConfig';
import CustomSelect from '../components/ui/CustomSelect';
import './Leaderboard.css';

/* ─────────────────────────────────────────────────────────────
   LEADERBOARD — ranked player lists with three views:

   Global  → all players sorted by ELO
   My Clubs → players in the user's clubs; chip toggles let
              them mix/filter multiple clubs at once
   Friends → placeholder (coming soon)
   ───────────────────────────────────────────────────────────── */

function CrownIcon() {
  return (
    <svg className="crown-icon" viewBox="0 0 24 24" width="18" height="18"
      fill="currentColor" aria-label="Rank 1">
      <path d="M5 16l-3-9 5.5 4L12 3l4.5 8L22 7l-3 9H5zm0 2h14v2H5v-2z"/>
    </svg>
  );
}

function VerifiedIcon() {
  return (
    <svg className="verified-icon" viewBox="0 0 24 24" width="13" height="13"
      fill="currentColor" aria-label="Verified club">
      <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
    </svg>
  );
}

function LoadingStars() {
  return (
    <div className="lb-loading">
      <span className="lb-loading-star">★</span>
      <span className="lb-loading-star">★</span>
      <span className="lb-loading-star">★</span>
    </div>
  );
}

function PlayerList({ players, currentUserId, showProvBadge = false, isDoubles = false }) {
  if (players.length === 0) {
    return (
      <div className="lb-empty">
        <p className="lb-empty-icon">★</p>
        <p className="lb-empty-title">No players yet</p>
        <p className="lb-empty-subtitle">Be the first to log a match.</p>
      </div>
    );
  }

  return (
    <div className="lb-list">
      <div className="lb-row lb-row-header">
        <span className="lb-col-rank">Rank</span>
        <span className="lb-col-name">Player</span>
        <span className="lb-col-rating">Rating</span>
        <span className="lb-col-record">W / L</span>
      </div>
      {players.map((player, index) => {
        const provisional = showProvBadge && isProvisional(player);
        return (
          <div
            key={player.id}
            className={[
              'lb-row lb-row-player',
              index === 0                 ? 'lb-row-first' : '',
              player.id === currentUserId ? 'lb-row-me'    : '',
              provisional                 ? 'lb-row-prov'  : '',
            ].join(' ').trim()}
          >
            <span className={`lb-col-rank ${provisional ? 'lb-rank-prov' : ''}`}>
              {index === 0 && !provisional ? <CrownIcon /> : `#${index + 1}`}
            </span>
            <span className="lb-col-name">
              <Link to={`/profile/${player.username}`} className="lb-player-link">
                {player.username}
              </Link>
              {player.id === currentUserId && (
                <span className="lb-you-badge">You</span>
              )}
              {provisional && (
                <span className="lb-prov-badge">ESTIMATED</span>
              )}
            </span>
            <span className="lb-col-rating">
              <span className="lb-rating-num">{isDoubles ? player.doubles_elo : player.elo_rating}</span>
            </span>
            <span className="lb-col-record">
              {isDoubles ? `${player.doubles_wins} / ${player.doubles_losses}` : `${player.wins} / ${player.losses}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// A player is shown on the leaderboard once they have:
// - At least 5 matches played
// - Reliability score >= 35 (blocks single-account farming)
// Reliability = min(100, 5 + matches_played + unique_opponents * 5)
function isProvisional(player) {
  if (player.matches_played < 5) return true;
  const reliability = Math.min(
    100,
    5 + player.matches_played + (player.unique_opponents_count ?? 0) * 5
  );
  return reliability < 35;
}

function Leaderboard() {
  const { user }   = useAuth();
  const navigate   = useNavigate();

  // ── Tab ─────────────────────────────────────────────────────
  const [tab, setTab] = useState('global');

  // ── Singles / Doubles mode (within Global tab) ───────────────
  const [lbMode, setLbMode] = useState('singles'); // 'singles' | 'doubles'
  const [doublesPlayers, setDoublesPlayers] = useState([]);
  const [doublesLoading, setDoublesLoading] = useState(false);

  // ── Friends player search ────────────────────────────────────
  const [rivalQuery,      setRivalQuery]      = useState('');
  const [rivalResults,    setRivalResults]    = useState([]);
  const [rivalStatuses,   setRivalStatuses]   = useState({});  // { [playerId]: 'pending' | 'accepted' }
  const rivalSearchRef = useRef(null);

  // ── Friends leaderboard ──────────────────────────────────────
  const [friendsList,       setFriendsList]       = useState([]);
  const [friendsLoading,    setFriendsLoading]    = useState(false);
  const [friendsFetched,    setFriendsFetched]    = useState(false); // has the first fetch run?

  // ── Pending incoming friend requests (Friends tab) ──────────
  const [pendingRequests, setPendingRequests] = useState([]);

  // ── Global tab ──────────────────────────────────────────────
  const [globalPlayers, setGlobalPlayers]   = useState([]);
  const [globalLoading, setGlobalLoading]   = useState(false);
  const [showAll,       setShowAll]         = useState(false); // toggle: false = verified only

  // ── My Clubs tab ────────────────────────────────────────────
  // userClubs: null = not yet fetched, [] = fetched but empty
  const [userClubs, setUserClubs]                   = useState(null);
  const [activeClubIds, setActiveClubIds]           = useState(new Set());
  const [clubPlayers, setClubPlayers]               = useState([]);
  const [clubPlayersLoading, setClubPlayersLoading] = useState(false);

  // ── Club search ─────────────────────────────────────────────
  const [showSearch, setShowSearch]       = useState(false);
  const [clubSearch, setClubSearch]       = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [joining, setJoining]             = useState({});

  // ── Create club form ────────────────────────────────────────
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName]   = useState('');
  const [createState, setCreateState] = useState('');
  const [createCity, setCreateCity]   = useState('');
  const [createType, setCreateType]   = useState('Public');
  const [creating, setCreating]             = useState(false);
  const [createError, setCreateError]       = useState('');

  // ── Success banner ───────────────────────────────────────────
  const [successMessage, setSuccessMessage] = useState('');

  // ── Fetch global players when tab or mode changes ───────────
  useEffect(() => {
    if (tab === 'global') {
      if (lbMode === 'doubles') fetchDoublesGlobal();
      else fetchGlobal();
    }
  }, [tab, lbMode]);

  // ── Fetch friends + pending requests when Friends tab opens ─
  useEffect(() => {
    if (tab === 'friends' && user) {
      fetchFriends();
      fetchPendingRequests();
    }
  }, [tab]);

  // ── Fetch user's clubs when My Clubs tab is first opened ────
  useEffect(() => {
    if (tab === 'clubs' && userClubs === null) fetchUserClubs();
  }, [tab]);

  // ── Re-fetch club players when active selection changes ─────
  useEffect(() => {
    if (activeClubIds.size > 0) {
      fetchClubPlayers([...activeClubIds]);
    } else {
      setClubPlayers([]);
    }
  }, [activeClubIds]);

  // ── Debounced rival player search ──────────────────────────
  useEffect(() => {
    if (rivalQuery.trim().length < 2) { setRivalResults([]); setRivalStatuses({}); return; }
    const timer = setTimeout(async () => {
      const { data: players } = await supabase
        .from('profiles')
        .select('id, username, elo_rating')
        .or(`full_name.ilike.%${sanitizeSearchQuery(rivalQuery.trim())}%,username.ilike.%${sanitizeSearchQuery(rivalQuery.trim())}%`)
        .neq('id', user.id)
        .limit(6);

      const found = players || [];

      // Fetch friendship statuses before showing results to avoid flash.
      // Two separate queries (sent + received) are more reliable than a
      // complex OR+AND PostgREST filter.
      let statusMap = {};
      if (user && found.length) {
        const ids = found.map(p => p.id);

        const [{ data: sent }, { data: received }] = await Promise.all([
          // Rows where current user sent the request
          supabase
            .from('friendships')
            .select('friend_id, status')
            .eq('user_id', user.id)
            .in('friend_id', ids),
          // Rows where current user received the request
          supabase
            .from('friendships')
            .select('user_id, status')
            .eq('friend_id', user.id)
            .in('user_id', ids),
        ]);

        (sent     || []).forEach(f => { statusMap[f.friend_id] = f.status; });
        (received || []).forEach(f => { statusMap[f.user_id]   = f.status; });
      }

      // Set both at once so they render together — no flash
      setRivalResults(found);
      setRivalStatuses(statusMap);
    }, 200);
    return () => clearTimeout(timer);
  }, [rivalQuery]);

  // ── Debounced club search ───────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clubSearch.trim().length >= 2) searchClubs(clubSearch);
      else setSearchResults([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [clubSearch, userClubs]);

  // ────────────────────────────────────────────────────────────
  // Data fetching
  // ────────────────────────────────────────────────────────────

  async function fetchFriends() {
    setFriendsLoading(true);

    // Two queries: friendships I initiated + friendships sent to me
    const [{ data: sent }, { data: received }] = await Promise.all([
      supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id)
        .eq('status', 'accepted'),
      supabase
        .from('friendships')
        .select('user_id')
        .eq('friend_id', user.id)
        .eq('status', 'accepted'),
    ]);

    const actualFriendIds = [
      ...(sent     || []).map(f => f.friend_id),
      ...(received || []).map(f => f.user_id),
    ];

    // Only show the leaderboard (including yourself) if you have at least one friend.
    // If you have no friends yet, leave the list empty so the empty state shows.
    if (actualFriendIds.length === 0) {
      setFriendsList([]);
      setFriendsLoading(false);
      setFriendsFetched(true);
      return;
    }

    const friendIds = [user.id, ...actualFriendIds];

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, elo_rating, wins, losses, matches_played, unique_opponents_count')
      .in('id', friendIds)
      .order('elo_rating', { ascending: false });

    setFriendsList(profiles || []);
    setFriendsLoading(false);
    setFriendsFetched(true);
  }

  async function fetchPendingRequests() {
    // Get friendships where I am the receiver and status is still pending
    const { data: friendships } = await supabase
      .from('friendships')
      .select('id, user_id')
      .eq('friend_id', user.id)
      .eq('status', 'pending');

    if (!friendships || friendships.length === 0) {
      setPendingRequests([]);
      return;
    }

    // Fetch the senders' profiles
    const senderIds = friendships.map(f => f.user_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, elo_rating')
      .in('id', senderIds);

    const profileMap = {};
    (profiles || []).forEach(p => { profileMap[p.id] = p; });

    setPendingRequests(
      friendships.map(f => ({
        friendshipId: f.id,
        senderId:     f.user_id,
        username:     profileMap[f.user_id]?.username  || 'Unknown',
        elo_rating:   profileMap[f.user_id]?.elo_rating ?? '—',
      }))
    );
  }

  async function fetchGlobal() {
    setGlobalLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, username, elo_rating, wins, losses, matches_played, unique_opponents_count')
      .order('elo_rating', { ascending: false });
    setGlobalPlayers(data || []);
    setGlobalLoading(false);
  }

  async function fetchDoublesGlobal() {
    setDoublesLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('id, username, doubles_elo, doubles_wins, doubles_losses, doubles_matches_played, doubles_unique_opponents_count')
      .gt('doubles_matches_played', 0)
      .order('doubles_elo', { ascending: false });
    setDoublesPlayers(data || []);
    setDoublesLoading(false);
  }

  async function fetchUserClubs() {
    const { data } = await supabase
      .from('club_memberships')
      .select('club_id, clubs(id, name, location, is_verified, club_type, created_by)')
      .eq('user_id', user.id);
    const clubs = (data || []).map(m => m.clubs).filter(Boolean);
    setUserClubs(clubs);
    if (clubs.length > 0) {
      setActiveClubIds(new Set(clubs.map(c => c.id)));
    }
  }

  async function fetchClubPlayers(clubIds) {
    setClubPlayersLoading(true);

    // Step 1 — get all user_ids that belong to any of the selected clubs
    const { data: memberships } = await supabase
      .from('club_memberships')
      .select('user_id')
      .in('club_id', clubIds);

    if (!memberships || memberships.length === 0) {
      setClubPlayers([]);
      setClubPlayersLoading(false);
      return;
    }

    // Deduplicate — a user in multiple selected clubs appears once
    const userIds = [...new Set(memberships.map(m => m.user_id))];

    // Step 2 — fetch those profiles sorted by rating
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, elo_rating, wins, losses, matches_played, unique_opponents_count')
      .in('id', userIds)
      .order('elo_rating', { ascending: false });

    setClubPlayers(profiles || []);
    setClubPlayersLoading(false);
  }

  async function searchClubs(query) {
    setSearching(true);

    // If the query matches the start of any state name (e.g. "Nevada" or "Nev"),
    // also search by those states' abbreviations so "Nevada" finds "Las Vegas, NV".
    const q = query.trim();
    const matchedAbbrs = q.length >= 2
      ? US_STATES
          .filter(s => s.name.toLowerCase().includes(q.toLowerCase()))
          .map(s => s.abbr)
      : [];

    const filterParts = [
      `name.ilike.%${q}%`,
      `location.ilike.%${q}%`,
      ...matchedAbbrs.map(abbr => `location.ilike.%${abbr}`),
    ];

    const { data } = await supabase
      .from('clubs')
      .select('id, name, location, is_verified')
      .or(filterParts.join(','))
      .order('is_verified', { ascending: false })
      .order('name')
      .limit(8);
    // Hide clubs the user is already in
    const memberIds = new Set((userClubs || []).map(c => c.id));
    setSearchResults((data || []).filter(c => !memberIds.has(c.id)));
    setSearching(false);
  }

  // ────────────────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────────────────

  async function joinClub(club) {
    setJoining(prev => ({ ...prev, [club.id]: true }));
    const { error } = await supabase
      .from('club_memberships')
      .insert({ user_id: user.id, club_id: club.id });
    if (!error) {
      const joined = { id: club.id, name: club.name, location: club.location, is_verified: club.is_verified };
      setUserClubs(prev => [...(prev || []), joined]);
      setActiveClubIds(prev => new Set([...prev, club.id]));
      setSearchResults(prev => prev.filter(c => c.id !== club.id));
    }
    setJoining(prev => ({ ...prev, [club.id]: false }));
  }

  async function createAndJoinClub() {
    if (!createName.trim() || !createState || !createCity) return;
    const createLocation = `${createCity}, ${createState}`;
    setCreating(true);
    setCreateError('');

    // Insert the new club
    const { data: newClub, error: clubErr } = await supabase
      .from('clubs')
      .insert({ name: createName.trim(), location: createLocation.trim(), club_type: createType, created_by: user.id })
      .select()
      .single();

    if (clubErr) {
      setCreateError(
        clubErr.message.includes('unique')
          ? 'A club with this name already exists. Try searching for it.'
          : 'Could not create club. Please try again.'
      );
      setCreating(false);
      return;
    }

    // Auto-join as a member
    await supabase
      .from('club_memberships')
      .insert({ user_id: user.id, club_id: newClub.id });

    setUserClubs(prev => [...(prev || []), newClub]);
    setActiveClubIds(prev => new Set([...prev, newClub.id]));
    setShowCreateForm(false);
    setShowSearch(false);
    setCreateName('');
    setCreateState('');
    setCreateCity('');
    setCreateType('Public');
    setCreating(false);
    setSuccessMessage(`You are now the owner of ${newClub.name}!`);
    setTimeout(() => setSuccessMessage(''), 5000);
  }

  async function handleRespondRival(friendshipId, accept) {
    await supabase.rpc('respond_to_friend_request', {
      p_friendship_id: friendshipId,
      p_accept:        accept,
    });
    setPendingRequests(prev => prev.filter(r => r.friendshipId !== friendshipId));
    // If accepted, pull the new friend into the leaderboard
    if (accept) fetchFriends();
  }

  function toggleChip(clubId) {
    setActiveClubIds(prev => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId);
      else next.add(clubId);
      return next;
    });
  }

  function openSearch() {
    setShowSearch(true);
    setShowCreateForm(false);
    setClubSearch('');
    setSearchResults([]);
    setCreateError('');
  }

  function closeSearch() {
    setShowSearch(false);
    setShowCreateForm(false);
    setClubSearch('');
    setSearchResults([]);
    setCreateError('');
    setCreateType('Public');
  }

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-inner">

        {/* Page header */}
        <div className="leaderboard-header">
          <h1 className="leaderboard-title">Leaderboard</h1>
        </div>

        {/* Success banner */}
        {successMessage && (
          <div className="lb-success-banner">
            {successMessage}
          </div>
        )}

        {/* Tab toggle */}
        <div className="lb-tabs">
          <button className={`lb-tab ${tab === 'global'  ? 'active' : ''}`} onClick={() => setTab('global')}>Global</button>
          <button className={`lb-tab ${tab === 'clubs'   ? 'active' : ''}`} onClick={() => setTab('clubs')}>My Clubs</button>
          <button className={`lb-tab ${tab === 'friends' ? 'active' : ''}`} onClick={() => setTab('friends')}>Friends</button>
        </div>

        {/* ── GLOBAL TAB ──────────────────────────────────────── */}
        {tab === 'global' && (
          <>
            {/* Singles / Doubles mode toggle */}
            <div className="lb-mode-toggle">
              <button
                className={`lb-mode-btn ${lbMode === 'singles' ? 'lb-mode-btn--active' : ''}`}
                onClick={() => setLbMode('singles')}
              >
                Singles
              </button>
              <button
                className={`lb-mode-btn ${lbMode === 'doubles' ? 'lb-mode-btn--active' : ''}`}
                onClick={() => setLbMode('doubles')}
              >
                Doubles
              </button>
            </div>

            {lbMode === 'doubles' ? (
              doublesLoading ? <LoadingStars /> : (
                doublesPlayers.length === 0 ? (
                  <div className="lb-empty">
                    <p className="lb-empty-icon">★</p>
                    <p className="lb-empty-title">No doubles matches yet</p>
                    <p className="lb-empty-subtitle">Be the first to log a doubles match.</p>
                  </div>
                ) : (
                  <PlayerList players={doublesPlayers} currentUserId={user?.id} isDoubles />
                )
              )
            ) : (
              globalLoading ? <LoadingStars /> : (
                <>
                  <div className="lb-filter-row">
                    <span className="lb-filter-label">
                      {showAll ? 'Showing all players' : 'Reliable ratings only'}
                    </span>
                    <button
                      className={`lb-toggle ${showAll ? 'lb-toggle-on' : ''}`}
                      onClick={() => setShowAll(v => !v)}
                      aria-label="Toggle unrated players"
                    >
                      <span className="lb-toggle-knob" />
                    </button>
                    <span className="lb-toggle-hint">Show all accounts</span>
                  </div>
                  <PlayerList
                    players={showAll ? globalPlayers : globalPlayers.filter(p => !isProvisional(p))}
                    currentUserId={user?.id}
                    showProvBadge={showAll}
                  />
                </>
              )
            )}
          </>
        )}

        {/* ── FRIENDS TAB ─────────────────────────────────────── */}
        {tab === 'friends' && (
          <>
            {friendsLoading && <LoadingStars />}

            {/* Friends leaderboard — shown when fetch is done and list is non-empty */}
            {friendsFetched && !friendsLoading && friendsList.length > 0 && (
              <>
                <PlayerList players={friendsList} currentUserId={user?.id} />

                {/* Add more rivals below the list */}
                <div className="lb-rival-search" ref={rivalSearchRef}>
                  <input
                    className="lb-search-input lb-rival-input"
                    placeholder="Add another rival..."
                    value={rivalQuery}
                    onChange={e => setRivalQuery(e.target.value)}
                  />
                  {rivalResults.length > 0 && (
                    <div className="lb-search-results lb-rival-results">
                      {rivalResults.map(p => (
                        <div key={p.id} className="lb-search-result">
                          <div className="lb-search-result-info">
                            <span className="lb-search-result-name">
                              <Link to={`/profile/${p.username}`} className="lb-rival-name-link">
                                {p.username}
                              </Link>
                              <span className="lb-rival-elo">({p.elo_rating})</span>
                            </span>
                          </div>
                          <button
                            className="lb-search-join-btn"
                            disabled={!!rivalStatuses[p.id]}
                            onClick={async () => {
                              await supabase.rpc('send_friend_request', { p_friend_id: p.id });
                              setRivalStatuses(prev => ({ ...prev, [p.id]: 'pending' }));
                            }}
                          >
                            {rivalStatuses[p.id] === 'accepted' ? 'Friends'
                              : rivalStatuses[p.id] === 'pending' ? 'Pending'
                              : 'Add Rival'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Pending incoming friend requests — always visible */}
            {friendsFetched && !friendsLoading && pendingRequests.length > 0 && (
              <div className="lb-pending-section">
                <p className="lb-pending-heading">Pending Friend Requests</p>
                <div className="lb-search-results lb-pending-list">
                  {pendingRequests.map(req => (
                    <div key={req.friendshipId} className="lb-search-result">
                      <div className="lb-search-result-info">
                        <span className="lb-search-result-name">
                          <Link to={`/profile/${req.username}`} className="lb-rival-name-link">
                            {req.username}
                          </Link>
                          <span className="lb-rival-elo">({req.elo_rating})</span>
                        </span>
                      </div>
                      <div className="lb-pending-actions">
                        <button
                          className="lb-pending-accept"
                          onClick={() => handleRespondRival(req.friendshipId, true)}
                        >
                          Accept
                        </button>
                        <button
                          className="lb-pending-decline"
                          onClick={() => handleRespondRival(req.friendshipId, false)}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state — only shown after fetch confirms zero entries */}
            {friendsFetched && !friendsLoading && friendsList.length === 0 && (
              <div className="lb-empty">
                <p className="lb-empty-icon">⚔️</p>
                <p className="lb-empty-title">No rivals yet</p>
                <p className="lb-empty-subtitle">Search for a rival to start your list.</p>
                <div className="lb-rival-search" ref={rivalSearchRef}>
                  <input
                    className="lb-search-input lb-rival-input"
                    placeholder="Search players..."
                    value={rivalQuery}
                    onChange={e => setRivalQuery(e.target.value)}
                  />
                  {rivalResults.length > 0 && (
                    <div className="lb-search-results lb-rival-results">
                      {rivalResults.map(p => (
                        <div key={p.id} className="lb-search-result">
                          <div className="lb-search-result-info">
                            <span className="lb-search-result-name">
                              <Link to={`/profile/${p.username}`} className="lb-rival-name-link">
                                {p.username}
                              </Link>
                              <span className="lb-rival-elo">({p.elo_rating})</span>
                            </span>
                          </div>
                          <button
                            className="lb-search-join-btn"
                            disabled={!!rivalStatuses[p.id]}
                            onClick={async () => {
                              await supabase.rpc('send_friend_request', { p_friend_id: p.id });
                              setRivalStatuses(prev => ({ ...prev, [p.id]: 'pending' }));
                            }}
                          >
                            {rivalStatuses[p.id] === 'accepted' ? 'Friends'
                              : rivalStatuses[p.id] === 'pending' ? 'Pending'
                              : 'Add Rival'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── MY CLUBS TAB ────────────────────────────────────── */}
        {tab === 'clubs' && (
          <>
            {/* Still fetching user's club list */}
            {userClubs === null && <LoadingStars />}

            {/* User has clubs */}
            {userClubs !== null && userClubs.length > 0 && (
              <>
                {/* Club chip toggles — only shown when in 2+ clubs */}
                {userClubs.length > 1 && (
                  <div className="lb-chips-wrap">
                    <div className="lb-chips">
                      {userClubs.map(club => (
                        <button
                          key={club.id}
                          className={`lb-chip ${activeClubIds.has(club.id) ? 'active' : ''}`}
                          onClick={() => toggleChip(club.id)}
                        >
                          {club.is_verified && <VerifiedIcon />}
                          {club.name}
                          {club.created_by === user?.id && (
                            <span className="lb-owner-badge">Owner</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <Link to="/clubs" className="lb-chip-view-link">
                      View Clubs →
                    </Link>
                  </div>
                )}

                {/* No chips selected */}
                {activeClubIds.size === 0 && (
                  <div className="lb-empty">
                    <p className="lb-empty-title">No clubs selected</p>
                    <p className="lb-empty-subtitle">Tap a club above to see its rankings.</p>
                  </div>
                )}

                {/* Club leaderboard */}
                {activeClubIds.size > 0 && (
                  clubPlayersLoading
                    ? <LoadingStars />
                    : <PlayerList players={clubPlayers} currentUserId={user?.id} />
                )}

                {/* Join another club */}
                {!showSearch && (
                  <button className="lb-add-club-btn" onClick={openSearch}>
                    + Join Another Club
                  </button>
                )}
              </>
            )}

            {/* User has no clubs yet */}
            {userClubs !== null && userClubs.length === 0 && !showSearch && (
              <div className="lb-empty">
                <p className="lb-empty-icon">★</p>
                <p className="lb-empty-title">You're not in any clubs yet</p>
                <p className="lb-empty-subtitle">Join a club to see how you rank against local players.</p>
                <button className="lb-add-club-btn lb-add-club-btn--centered" onClick={openSearch}>
                  Find Your Club
                </button>
              </div>
            )}

            {/* ── Club search / create panel ─────────────────── */}
            {showSearch && (
              <div className="lb-search-panel">
                <div className="lb-search-header">
                  <p className="lb-search-title">Find a Club</p>
                  <button className="lb-search-close" onClick={closeSearch} aria-label="Close">
                    &times;
                  </button>
                </div>

                {!showCreateForm && (
                  <>
                    <input
                      className="lb-search-input"
                      type="text"
                      placeholder="Search by club name…"
                      value={clubSearch}
                      onChange={e => setClubSearch(e.target.value)}
                      autoFocus
                    />

                    {/* Search results */}
                    {searching && (
                      <p className="lb-search-hint">Searching…</p>
                    )}

                    {!searching && clubSearch.length >= 2 && searchResults.length === 0 && (
                      <p className="lb-search-hint">No clubs found matching "{clubSearch}".</p>
                    )}

                    {searchResults.length > 0 && (
                      <div className="lb-search-results">
                        {searchResults.map(club => (
                          <div key={club.id} className="lb-search-result">
                            <div className="lb-search-result-info">
                              <span className="lb-search-result-name">
                                {club.name}
                                {club.is_verified && <VerifiedIcon />}
                              </span>
                              <span className="lb-search-result-location">{club.location}</span>
                            </div>
                            <button
                              className="lb-search-join-btn"
                              onClick={() => joinClub(club)}
                              disabled={joining[club.id]}
                            >
                              {joining[club.id] ? 'Joining…' : 'Join'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      className="lb-create-link"
                      onClick={() => setShowCreateForm(true)}
                    >
                      Don't see your club? Create a new one
                    </button>
                  </>
                )}

                {/* Create club form */}
                {showCreateForm && (
                  <div className="lb-create-form">
                    <p className="lb-create-subtitle">
                      Give your club a name and location so others can find it.
                    </p>
                    <input
                      className="lb-search-input"
                      type="text"
                      placeholder="Club name (e.g. Austin TTC)"
                      value={createName}
                      onChange={e => setCreateName(e.target.value)}
                      maxLength={80}
                      autoFocus
                    />
                    <CustomSelect
                      value={createState}
                      onChange={v => { setCreateState(v); setCreateCity(''); }}
                      placeholder="Select a state…"
                      options={[
                        { value: '', label: 'Select a state…' },
                        ...US_STATES.map(s => ({ value: s.abbr, label: s.name })),
                      ]}
                    />
                    <CustomSelect
                      value={createCity}
                      onChange={v => setCreateCity(v)}
                      placeholder={createState ? 'Select a city…' : 'Select a state first'}
                      disabled={!createState}
                      options={[
                        { value: '', label: createState ? 'Select a city…' : 'Select a state first' },
                        ...(createState ? (CITIES_BY_STATE[createState] || []).map(city => ({ value: city, label: city })) : []),
                      ]}
                    />
                    <CustomSelect
                      value={createType}
                      onChange={v => setCreateType(v)}
                      placeholder="Club type…"
                      options={[
                        { value: 'Public',    label: 'Public'    },
                        { value: 'School',    label: 'School'    },
                        { value: 'Corporate', label: 'Corporate' },
                        { value: 'Community', label: 'Community' },
                      ]}
                    />
                    {createError && <p className="lb-create-error">{createError}</p>}
                    <div className="lb-create-actions">
                      <button
                        className="lb-create-back"
                        onClick={() => { setShowCreateForm(false); setCreateError(''); setCreateState(''); setCreateCity(''); }}
                      >
                        Back
                      </button>
                      <button
                        className="lb-create-submit"
                        onClick={createAndJoinClub}
                        disabled={creating || !createName.trim() || !createState || !createCity}
                      >
                        {creating ? 'Creating…' : 'Create & Join'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}

export default Leaderboard;
